import { OnchainAccount, OnchainAccountBorrowable, OnchainAccountCollateral, OnchainBorrowable, OnchainCollateral, OnchainInteractions, OnchainInteractionsBorrowable, OnchainInteractionsCollateral, OnchainPoolToken, PoolTokenType, onchain } from 'impermax-sdk';
import { useContext, useMemo } from 'react';
import { OnchainContext } from '../contexts/OnchainProvider';
import useFactory from './useFactory';
import useGenericCall from './useGenericCall';
import usePairAddress from './usePairAddress';
import usePoolToken from './usePoolToken';
import { useWallet } from 'use-wallet';

export default function useOnchainInstance() {
  const { onchainInstance } = useContext(OnchainContext);
  return onchainInstance;
};

export function useOnchainUpdate() {
  const { onchainUpdate } = useContext(OnchainContext);
  return onchainUpdate;
}

export function useDoUpdate() {
  const { doUpdate } = useContext(OnchainContext);
  return doUpdate;
}

export function usePriceInverted() {
  const { priceInverted } = useContext(OnchainContext);
  return priceInverted;
}

export function useTogglePriceInverted() {
  const { togglePriceInverted } = useContext(OnchainContext);
  return togglePriceInverted;
}

export function useOnchainFactory() {
  const factory = useFactory();
  const onchainInstance = useOnchainInstance();
  return onchainInstance ? onchainInstance.getImpermaxFactory(factory) : null;
}

export function useOnchainLendingPool() {
  const pairAddress = usePairAddress();
  const onchainFactory = useOnchainFactory();
  return onchainFactory ? onchainFactory.getLendingPool(pairAddress) : null;
}

export function useOnchainPoolToken(poolTokenTypeArg?: PoolTokenType) : OnchainPoolToken {
  const poolTokenType = poolTokenTypeArg ? poolTokenTypeArg : usePoolToken();
  const lendingPool = useOnchainLendingPool();
  return lendingPool ? lendingPool.getPoolToken(poolTokenType) : null;
}

export function useOnchainBorrowable(poolTokenTypeArg?: PoolTokenType) : OnchainBorrowable {
  return useOnchainPoolToken(poolTokenTypeArg) as OnchainBorrowable;
}

export function useOnchainCollateral() : OnchainCollateral {
  return useOnchainPoolToken(PoolTokenType.Collateral) as OnchainCollateral;
}

export function useOnchainAccount() : OnchainAccount {
  const onchainInstance = useOnchainInstance();
  const { account } = useWallet();
  return onchainInstance ? onchainInstance.getAccount(account) : null;
}

export function useOnchainAccountLendingPool() {
  const factory = useFactory();
  const pairAddress = usePairAddress();
  const onchainAccount = useOnchainAccount();
  return useGenericCall(onchainAccount, async onchainAccount => onchainAccount.getLendingPool(factory, pairAddress));
}

export function useOnchainAccountPoolToken(poolTokenTypeArg?: PoolTokenType) {
  const poolTokenType = poolTokenTypeArg ? poolTokenTypeArg : usePoolToken();
  const accountLendingPool = useOnchainAccountLendingPool();
  return accountLendingPool ? accountLendingPool.getPoolToken(poolTokenType) : null;
}

export function useOnchainAccountBorrowable(poolTokenTypeArg?: PoolTokenType) : OnchainAccountBorrowable {
  return useOnchainAccountPoolToken(poolTokenTypeArg) as OnchainAccountBorrowable;
}

export function useOnchainAccountCollateral() : OnchainAccountCollateral {
  return useOnchainAccountPoolToken(PoolTokenType.Collateral) as OnchainAccountCollateral;
}

export function useOnchainInteractions() : OnchainInteractions {
  const onchainAccount = useOnchainAccount();
  return onchainAccount ? onchainAccount.getInteractions() : null;
}

export function useOnchainInteractionsLendingPool() {
  const factory = useFactory();
  const pairAddress = usePairAddress();
  const onchainInteractions = useOnchainInteractions();
  return useGenericCall(onchainInteractions, async onchainInteractions => onchainInteractions.getLendingPool(factory, pairAddress));
}

export function useOnchainInteractionsPoolToken(poolTokenTypeArg?: PoolTokenType) {
  const poolTokenType = poolTokenTypeArg ? poolTokenTypeArg : usePoolToken();
  const interactionsLendingPool = useOnchainInteractionsLendingPool();
  return interactionsLendingPool ? interactionsLendingPool.getPoolToken(poolTokenType) : null;
}

export function useOnchainInteractionsBorrowable(poolTokenTypeArg?: PoolTokenType) : OnchainInteractionsBorrowable {
  return useOnchainInteractionsPoolToken(poolTokenTypeArg) as OnchainInteractionsBorrowable;
}

export function useOnchainInteractionsCollateral() : OnchainInteractionsCollateral {
  return useOnchainInteractionsPoolToken(PoolTokenType.Collateral) as OnchainInteractionsCollateral;
}